/**
 * @file contacts-edit.ts
 * @description Правка контактов и соцсетей на «Настройках сайта» (phase19.16
 * Р1; SB18, SB21, SB58, SB85; реестр видов `SITE_CONTACT_KINDS` пакета
 * shared). Контакты и соцсети хранятся одним списком `brand.contacts`, а
 * на экране — двумя разделами: «Контакты» (телефон, почта, адрес) и
 * «Соцсети» (виды с `social: true`).
 *
 * На сервер уходит только список, где каждое значение проходит разбор
 * реестра: иначе сервер отклонит всю правку бренда (400), и вместе с
 * ошибочной соцсетью не сохранится, например, новый слоган.
 */

import {
  parseSiteContactValue,
  SITE_CONTACT_KIND_KEYS,
  SITE_CONTACT_KINDS,
  type SiteContactItem,
  type SiteContactKind,
} from "@chaiberi/shared";

/** Виды раздела «Соцсети» — из реестра, по признаку `social`. */
export const SOCIAL_KINDS: readonly SiteContactKind[] = SITE_CONTACT_KIND_KEYS.filter((k) => SITE_CONTACT_KINDS[k].social);
/** Виды раздела «Контакты». */
export const PLAIN_KINDS: readonly SiteContactKind[] = SITE_CONTACT_KIND_KEYS.filter((k) => !SITE_CONTACT_KINDS[k].social);

/** Строка правки: значение как ввёл человек. */
export interface ContactRow extends SiteContactItem {
  /** Ключ строки в списке (порядок меняется — индекс не годится). */
  key: string;
}

let seq = 0;
/** Ключ новой строки. */
export function rowKey(): string {
  seq += 1;
  return `c${seq}`;
}

export function toRows(items: readonly SiteContactItem[]): ContactRow[] {
  return items.map((item) => ({ ...item, key: rowKey() }));
}

/** Ошибка значения строки (`null` — значение подходит). Пустое значение — тоже ошибка, кроме почты. */
export function contactError(row: SiteContactItem): string | null {
  if (row.value === null || row.value.trim() === "") {
    return row.kind === "email" ? null : "Укажите значение или удалите строку";
  }
  const parsed = parseSiteContactValue(row.kind, row.value);
  return parsed.ok ? null : parsed.message;
}

/**
 * Список для сервера или `null`, если есть ошибки (тогда не сохраняем).
 * Строки разделов склеиваются: сначала контакты, затем соцсети.
 */
export function contactsForSave(rows: readonly ContactRow[]): SiteContactItem[] | null {
  if (rows.some((row) => contactError(row) !== null)) return null;
  return rows.map(({ kind, value, showInFooter, showInMenu }) => ({
    kind,
    value: value === null || value.trim() === "" ? null : value.trim(),
    showInFooter,
    showInMenu,
  }));
}

/** Новая строка вида: в подвале — да, в меню телефона — только соцсети и телефон. */
export function newContactRow(kind: SiteContactKind): ContactRow {
  const social = SITE_CONTACT_KINDS[kind].social;
  return { key: rowKey(), kind, value: kind === "email" ? null : "", showInFooter: true, showInMenu: social || kind === "phone" };
}

/** Поменять местами строку с соседней (порядок внутри раздела). */
export function moveRow(rows: readonly ContactRow[], key: string, delta: -1 | 1): ContactRow[] {
  const index = rows.findIndex((r) => r.key === key);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/**
 * Список в каноническом виде (значения — как их разбирает реестр): сервер
 * может привести «8 900…» к «+7900…», и это не повод пересобирать строки.
 */
export function sameContacts(a: readonly SiteContactItem[], b: readonly SiteContactItem[]): boolean {
  const canon = (items: readonly SiteContactItem[]) =>
    JSON.stringify(
      items.map((item) => {
        const parsed = item.value ? parseSiteContactValue(item.kind, item.value) : null;
        return [item.kind, parsed?.ok ? parsed.value : item.value, item.showInFooter, item.showInMenu];
      }),
    );
  return canon(a) === canon(b);
}
